import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { OutlinedBadge } from '@tms/shared-ui';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ContentPageLayout } from '../components/Shared';
import { useLanguage } from '../hooks/useLanguage';
import {
  createDocument,
  deleteDocument,
  duplicateDocument,
  getDocumentDraft,
  listDocuments,
  updateDocument,
  type DocumentRow,
} from '../services/documentApi';

const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'http://app.localtest.me:3001';
const DOC_TYPES = ['Lesson', 'Exercise', 'Song', 'Other'];
const DOC_DOMAINS = ['ST', 'M', 'MD', 'ACC', 'SONG', 'EX', 'Other'];
const STATUSES = ['draft', 'published', 'archived'];
const SHARING = ['default', 'public', 'private'];

type LiveFilter = 'all' | 'live' | 'draftAhead' | 'notPublished';

const liveState = (document: DocumentRow): Exclude<LiveFilter, 'all'> => {
  if (document.publishedVersion == null) return 'notPublished';
  if (document.currentVersion != null && document.currentVersion > document.publishedVersion) {
    return 'draftAhead';
  }
  return 'live';
};

const PageEditorAdminPage: React.FC = () => {
  const { navigateWithLang } = useLanguage();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [liveFilter, setLiveFilter] = useState<LiveFilter>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentRow | null>(null);
  const [form, setForm] = useState({
    title: '',
    code: '',
    docType: 'Lesson',
    docDomain: 'ST',
    status: 'draft',
    sharingRule: 'default',
    description: '',
  });

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) setSearch(query);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listDocuments({ limit: 500, sortBy: 'updatedAt', sortOrder: 'desc' });
      setDocuments(res.data?.documents || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('documentsAdmin.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const searched = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter((document) => (
      document.title?.toLowerCase().includes(query)
      || document.code?.toLowerCase().includes(query)
    ));
  }, [documents, search]);

  const liveCounts = useMemo(() => ({
    all: searched.length,
    live: searched.filter((document) => liveState(document) === 'live').length,
    draftAhead: searched.filter((document) => liveState(document) === 'draftAhead').length,
    notPublished: searched.filter((document) => liveState(document) === 'notPublished').length,
  }), [searched]);

  const filtered = useMemo(
    () => searched.filter((document) => liveFilter === 'all' || liveState(document) === liveFilter),
    [searched, liveFilter],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: '',
      code: '',
      docType: 'Lesson',
      docDomain: 'ST',
      status: 'draft',
      sharingRule: 'default',
      description: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (document: DocumentRow) => {
    setEditing(document);
    setForm({
      title: document.title || '',
      code: document.code || '',
      docType: document.docType || 'Lesson',
      docDomain: document.docDomain || 'ST',
      status: document.publishedVersion ? 'published' : (document.status || 'draft'),
      sharingRule: document.sharingRule || 'default',
      description: document.description || '',
    });
    setDialogOpen(true);
    void getDocumentDraft(document._id)
      .then((res) => {
        const draft = res.data?.document;
        if (!draft) return;
        setEditing({ ...document, ...draft, _id: document._id });
        setForm((current) => ({
          ...current,
          title: draft.title || current.title,
          code: draft.code || current.code,
          description: draft.description || '',
          status: draft.publishedVersion ? 'published' : (draft.status || current.status),
        }));
      })
      .catch(() => undefined);
  };

  const openEditor = (documentId?: string) => {
    navigateWithLang(documentId ? `/page-editor?document=${encodeURIComponent(documentId)}` : '/page-editor');
  };

  const handleSave = async () => {
    if (form.status === 'published' && (!editing || !editing.publishedVersion)) {
      setMsg(t('documentsAdmin.publishRequiresVersion'));
      return;
    }
    try {
      if (editing) {
        await updateDocument(editing._id, {
          title: form.title,
          code: form.code,
          docType: form.docType,
          docDomain: form.docDomain,
          status: form.status,
          sharingRule: form.sharingRule,
          description: form.description,
        });
        setMsg(t('documentsAdmin.updated'));
      } else {
        await createDocument({
          title: form.title,
          code: form.code,
          docType: form.docType,
          docDomain: form.docDomain,
          status: form.status,
          sharingRule: form.sharingRule,
          description: form.description,
          content: { components: [] },
          edition: { version: '1.0.0' },
        });
        setMsg(t('documentsAdmin.created'));
      }
      setDialogOpen(false);
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : t('documentsAdmin.saveFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('documentsAdmin.deleteConfirm'))) return;
    try {
      await deleteDocument(id);
      setMsg(t('documentsAdmin.deleted'));
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : t('documentsAdmin.deleteFailed'));
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateDocument(id, {});
      setMsg(t('documentsAdmin.duplicated'));
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : t('documentsAdmin.duplicateFailed'));
    }
  };

  const authorLabel = (document: DocumentRow) => document.author?.nickname || document.author?.email || '—';

  const liveBadges = (document: DocumentRow) => {
    if (document.publishedVersion == null) {
      return <OutlinedBadge label={t('documentsAdmin.notPublished')} />;
    }
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <OutlinedBadge
          label={t('documentsAdmin.liveVersion', { version: document.publishedVersion })}
          color="success"
        />
        {document.currentVersion != null && document.currentVersion > document.publishedVersion ? (
          <OutlinedBadge label={t('documentsAdmin.draftAhead')} color="secondary" />
        ) : null}
      </Box>
    );
  };

  return (
    <ContentPageLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2.5}>
          <Box>
            <Typography variant="h5" color="primary" fontWeight="bold">{t('documentsAdmin.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
              {t('documentsAdmin.subtitle')}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<EditNoteIcon />} onClick={() => openEditor()}>
              {t('documentsAdmin.openEditor')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('documentsAdmin.create')}
            </Button>
          </Box>
        </Box>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('documentsAdmin.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label={t('documentsAdmin.search')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={liveFilter}
                onChange={(_event, value: LiveFilter | null) => {
                  if (value) setLiveFilter(value);
                }}
              >
                <ToggleButton value="all">{t('documentsAdmin.statusAll')} · {liveCounts.all}</ToggleButton>
                <ToggleButton value="live">{t('documentsAdmin.live')} · {liveCounts.live}</ToggleButton>
                <ToggleButton value="draftAhead">{t('documentsAdmin.draftAhead')} · {liveCounts.draftAhead}</ToggleButton>
                <ToggleButton value="notPublished">{t('documentsAdmin.notPublished')} · {liveCounts.notPublished}</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {filtered.length === documents.length
                  ? t('documentsAdmin.results', { count: filtered.length })
                  : t('documentsAdmin.resultsFiltered', { shown: filtered.length, total: documents.length })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 7, px: 3, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {documents.length === 0 ? t('documentsAdmin.emptyNone') : t('documentsAdmin.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('documentsAdmin.emptyHint')}
            </Typography>
            {documents.length === 0 ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                {t('documentsAdmin.create')}
              </Button>
            ) : null}
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('documentsAdmin.colDocument')}</TableCell>
                  <TableCell>{t('documentsAdmin.colType')}</TableCell>
                  <TableCell>{t('documentsAdmin.colDomain')}</TableCell>
                  <TableCell>{t('documentsAdmin.colLive')}</TableCell>
                  <TableCell>{t('documentsAdmin.colAuthor')}</TableCell>
                  <TableCell align="right">{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((document) => (
                  <TableRow
                    key={document._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openEditor(document._id)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {document.title || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {document.code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{document.docType || '—'}</TableCell>
                    <TableCell>{document.docDomain || '—'}</TableCell>
                    <TableCell>{liveBadges(document)}</TableCell>
                    <TableCell>{authorLabel(document)}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <Tooltip title={t('documentsAdmin.preview')}>
                        <IconButton
                          size="small"
                          href={`${APP_ORIGIN.replace(/\/$/, '')}/doc/${document._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t('documentsAdmin.preview')}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('documentsAdmin.openEditor')}>
                        <IconButton
                          size="small"
                          onClick={() => openEditor(document._id)}
                          aria-label={t('documentsAdmin.openEditor')}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('documentsAdmin.editMeta')}>
                        <IconButton
                          size="small"
                          onClick={() => openEdit(document)}
                          aria-label={t('documentsAdmin.editMeta')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('documentsAdmin.duplicate')}>
                        <IconButton
                          size="small"
                          onClick={() => void handleDuplicate(document._id)}
                          aria-label={t('documentsAdmin.duplicate')}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void handleDelete(document._id)}
                          aria-label={t('delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editing ? t('documentsAdmin.dialogEdit') : t('documentsAdmin.dialogCreate')}</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField label={t('documentsAdmin.fieldTitle')} required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} />
              <TextField label={t('documentsAdmin.fieldCode')} required value={form.code} onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))} />
              <TextField select label={t('documentsAdmin.fieldType')} value={form.docType} onChange={(event) => setForm((current) => ({ ...current, docType: event.target.value }))}>
                {DOC_TYPES.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField select label={t('documentsAdmin.fieldDomain')} value={form.docDomain} onChange={(event) => setForm((current) => ({ ...current, docDomain: event.target.value }))}>
                {DOC_DOMAINS.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField
                select
                label={t('documentsAdmin.fieldStatus')}
                value={form.status}
                onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                helperText={t('documentsAdmin.statusHint')}
              >
                {STATUSES.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField select label={t('documentsAdmin.fieldSharing')} value={form.sharingRule} onChange={(event) => setForm((current) => ({ ...current, sharingRule: event.target.value }))}>
                {SHARING.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField
                label={t('documentsAdmin.fieldDescription')}
                multiline
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t('documentsAdmin.cancel')}</Button>
            <Button variant="contained" onClick={() => void handleSave()} disabled={!form.title.trim() || !form.code.trim()}>
              {t('documentsAdmin.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ContentPageLayout>
  );
};

export default PageEditorAdminPage;
